import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, User, ArrowLeft, Tag, Share2, Play, Film } from 'lucide-react';
import { Button } from '../components/ui/button';
import AppLink from '../components/AppLink';

const NewsDetail = () => {
  const { id } = useParams();
  const { language, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`/api/news/${id}`);
        if (res.ok) setArticle(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h2 className="text-2xl font-bold text-gray-700">Article not found</h2>
        <AppLink to="/news">
          <Button className="bg-party-red hover:bg-party-red-600"><ArrowLeft className="h-4 w-4 mr-2" /> Back to News</Button>
        </AppLink>
      </div>
    );
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = article ? (typeof article.title === 'object' ? t(article.title) : article.title) : '';

  return (
    <div className="min-h-screen" data-testid="news-detail-page">
      {/* Hero */}
      <section className="relative h-[400px] overflow-hidden">
        <img src={article.image_url} alt={t(article.title)} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            {article.category && (
              <span className="inline-flex items-center gap-1 bg-party-red text-white px-3 py-1 rounded-full text-sm mb-4">
                <Tag className="h-3 w-3" />{t(article.category)}
              </span>
            )}
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t(article.title)}</h1>
            <div className="flex items-center gap-6 text-gray-300 text-sm">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(article.date || article.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'bn-BD', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              {article.author && (
                <span className="flex items-center gap-2"><User className="h-4 w-4" />{article.author}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <AppLink to="/news">
              <Button variant="outline" size="sm" className="text-party-red border-party-red hover:bg-party-red hover:text-white" data-testid="back-to-news-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to News
              </Button>
            </AppLink>
            <ShareButtons url={shareUrl} text={shareText} />
          </div>

          {article.excerpt && (
            <p className="text-xl text-gray-600 font-medium mb-8 leading-relaxed border-l-4 border-party-red pl-6">
              {t(article.excerpt)}
            </p>
          )}

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
            {t(article.content)}
          </div>

          {/* Embedded Photo & Video Gallery */}
          {article.images && article.images.length > 0 && (
            <div className="mt-12" data-testid="article-photo-gallery">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Tag className="h-5 w-5 text-party-red" />
                {t({ en: 'Photos & Videos from the Event', bn: 'অনুষ্ঠানের ছবি ও ভিডিও' })}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {article.images.map((img, idx) => {
                  const isVideo = img.type === 'video' || /\.(mp4|webm|mov)$/i.test(img.url || '');
                  return (
                    <div key={idx} className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                      {isVideo ? (
                        <>
                          <video src={img.url} className="w-full h-full object-cover" muted preload="metadata" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors cursor-pointer" onClick={(e) => { const vid = e.currentTarget.previousSibling; vid.controls = true; vid.muted = false; vid.play(); e.currentTarget.style.display = 'none'; }}>
                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                              <Play className="h-7 w-7 text-party-red ml-1" />
                            </div>
                          </div>
                          <div className="absolute top-2 left-2 bg-party-red text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Film className="h-3 w-3" /> Video
                          </div>
                        </>
                      ) : (
                        <img src={img.url} alt={t(img.caption) || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      )}
                      {img.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-sm font-medium">{t(img.caption)}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;

const ShareButtons = ({ url, text }) => {
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + url)}`;
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <span className="text-gray-400 text-sm mr-1 hidden sm:inline"><Share2 className="h-4 w-4" /></span>
      <a href={fbUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1877F2] text-white text-xs font-medium hover:opacity-90 transition-opacity" data-testid="share-facebook">
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        Facebook
      </a>
      <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] text-white text-xs font-medium hover:opacity-90 transition-opacity" data-testid="share-whatsapp">
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp
      </a>
    </div>
  );
};
